import { FC, useContext, useEffect, useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";

import { NumberPortingChoice } from "@/components/Checkout/Steps/NumberPorting/NumberPortingChoice.tsx";
import { LoadingSpinner } from "@/components/Layout/LoadingSpinner/LoadingSpinner";
import { DataContext } from "@/provider/DataContextProvider";
import { useGetCustomer, useGetOnpProviders } from "@/services/api";
import { Provider } from "@/services/model";

import {
  mnpContractType,
  SWYPE_PREPAID_PROVIDER_ID,
  SWYPE_PREPAID_PROVIDER_NAME,
} from "./NumberPorting.utils";
import { NumberPortingFormFields } from "./NumberPortingFormFields";
import { NumberPortingNavigation } from "./NumberPortingNavigation";
import {
  MSISDN_VALIDATION_STATUS,
  useMsisdnValidation,
} from "./useMsisdnValidation";
import { useNumberPortingActions } from "./useNumberPortingActions";

/**
 * Data structure for the Number Porting form.
 */
export type NumberPortingFormProps = {
  /** The phone number to be ported. */
  currentNumber: string;
  /** The current service provider name. */
  currentProvider: string;
  /** Contract type: "1" for postpaid, "2" for prepaid. */
  mnpContractType: "1" | "2";
  /** Porting timing: "2" for after contract ends, "3" for premature termination. */
  mnpTime: "2" | "3";
  /** Optional custom wish date for porting. */
  mnpWishDate?: string;
  /** User choice: "yes" to port number, "no" to get a new one. */
  enableNumberPorting: "yes" | "no";
};

/**
 * Helper to convert an array of Provider objects into a record of name -> id.
 */
const providersToObj = (arr: Provider[]): Record<string, number> =>
  Object.fromEntries(
    arr
      .filter(({ providerName, id }) => providerName && id)
      .map(({ providerName, id }) => [providerName, id]),
  );

/**
 * Main component for the Number Porting onboarding step.
 * Coordinates MSISDN validation, provider selection, and form submission.
 *
 * @param missingInfo - If true, the component is rendered in the 'Missing Info' flow in the Portal.
 */
export const NumberPorting: FC<{ missingInfo?: boolean }> = ({
  missingInfo,
}) => {
  const { dataContext } = useContext(DataContext);
  const [showNumberPortingError, setShowNumberPortingError] = useState(false);

  // Fetch providers and customer data.
  const { data: providersData, isFetching: isLoadingProviders } =
    useGetOnpProviders();
  const { data: customerData } = useGetCustomer();
  const providers = useMemo(
    () => providersData && providersToObj(providersData),
    [providersData],
  );

  // Initialize form with default values from context.
  const methods = useForm<NumberPortingFormProps>({
    mode: "onChange",
    defaultValues: dataContext.numberPorting,
  });

  const { watch, resetField, setValue, handleSubmit } = methods;

  // Hooks for MSISDN validation and submission actions.
  const {
    msisdnValidationStatus,
    msisdnError,
    setMsisdnError,
    validateMsisdn,
  } = useMsisdnValidation();

  const wishdateSelectable =
    (customerData as any)?.wishdateSelectable === true ||
    (customerData as any)?.customData?.wishdateSelectable === true;

  const {
    onSubmit,
    submitNumberPorting,
    isSettingMnpData,
    isClearingOnp,
    isCompletingMnpData,
  } = useNumberPortingActions(methods, providers, wishdateSelectable);

  // Watch form values for dynamic logic.
  const enableNumberPortingValue = watch("enableNumberPorting");
  const currentProvider = watch("currentProvider");
  const currentProviderId = currentProvider
    ? providers?.[currentProvider]
    : undefined;

  // Special handling for SWYPE provider (only Prepaid allowed).
  const isSwypePrepaidProviderSelected =
    currentProviderId === SWYPE_PREPAID_PROVIDER_ID ||
    currentProvider?.trim().toLowerCase() === SWYPE_PREPAID_PROVIDER_NAME;

  useEffect(() => {
    if (!isSwypePrepaidProviderSelected) {
      return;
    }

    // Force Prepaid if SWYPE is selected.
    setValue("mnpContractType", `${mnpContractType.prepaid.id}` as "2");
    resetField("mnpTime");
    resetField("mnpWishDate");
  }, [isSwypePrepaidProviderSelected, resetField, setValue]);

  const formValues = watch();
  const terminationBeforeContractEnd = formValues?.mnpTime?.toString() === "3";
  const hasWishDateWhenNeeded =
    !terminationBeforeContractEnd ||
    !wishdateSelectable ||
    !!formValues?.mnpWishDate;

  // Logic to disable the 'Complete' or 'Forward' button.
  const completeButtonDisabled =
    enableNumberPortingValue === "no"
      ? false
      : enableNumberPortingValue === "yes"
        ? missingInfo
          ? !(
              formValues?.currentNumber &&
              formValues.currentProvider &&
              formValues.mnpContractType &&
              (formValues.mnpContractType?.toString() !== "1" ||
                (formValues.mnpTime && hasWishDateWhenNeeded)) &&
              msisdnValidationStatus === MSISDN_VALIDATION_STATUS.VALID
            )
          : !(
              dataContext.basket?.id &&
              formValues?.currentNumber &&
              formValues.currentProvider &&
              formValues.mnpContractType &&
              (formValues.mnpContractType?.toString() !== "1" ||
                (formValues.mnpTime && hasWishDateWhenNeeded)) &&
              msisdnValidationStatus === MSISDN_VALIDATION_STATUS.VALID
            )
        : true;

  return (
    <div
      id="onboarding-number-porting"
      data-testid="onboarding-number-porting"
      className="flex w-full flex-col gap-6"
    >
      {isLoadingProviders && <LoadingSpinner />}

      <FormProvider {...methods}>
        {/* Step choice (Yes/No) - only shown if NOT in 'Missing Info' flow */}
        {!missingInfo && (
          <NumberPortingChoice showError={showNumberPortingError} />
        )}

        {/* Detailed form fields - shown if user wants to port their number */}
        {enableNumberPortingValue === "yes" && (
          <NumberPortingFormFields
            providers={providers}
            isSwypePrepaidProviderSelected={isSwypePrepaidProviderSelected}
            msisdnValidationStatus={msisdnValidationStatus}
            msisdnError={msisdnError}
            wishdateSelectable={wishdateSelectable}
            validateMsisdn={validateMsisdn}
            clearMsisdnError={() => setMsisdnError("")}
          />
        )}
      </FormProvider>

      <NumberPortingNavigation
        missingInfo={missingInfo}
        completeButtonDisabled={completeButtonDisabled}
        isSubmitting={isSettingMnpData || isClearingOnp || isCompletingMnpData}
        enableNumberPortingValue={enableNumberPortingValue}
        onForward={handleSubmit(onSubmit)}
        onComplete={() =>
          submitNumberPorting(formValues, msisdnValidationStatus)
        }
        setShowNumberPortingError={setShowNumberPortingError}
      />
    </div>
  );
};
